import ApolloClient from '@/packages/apollo-client'
import JWTDecode from '@/packages/jwt-decode'
import Logger from '@/packages/logger'

import ObtainTokensMutation from './Schema/Mutations/ObtainTokens.gql'
import RefreshTokensMutation from './Schema/Mutations/RefreshTokens.gql'
import DestroySessionMutation from './Schema/Mutations/DestroySession.gql'
import PasswordChallengeMutation from './Schema/Mutations/PasswordChallenge.gql'
import EditUserMutation from './Schema/Mutations/EditUser.gql'
import ChangePasswordMutation from './Schema/Mutations/ChangePassword.gql'
import FetchUserDataQuery from './Schema/Queries/FetchUserData.gql'
import MfaStatusQuery from './Schema/Queries/MfaStatus.gql'
import SetDepartment from './Schema/Mutations/SetDepartment.gql'
import SetShippingRules from './Schema/Mutations/SetShippingRules.gql'
import ForgotPassword from './Schema/Mutations/ForgotPassword.gql'
import ResetPassword from './Schema/Mutations/ResetPassword.gql'
import MfaAssociateMutation from './Schema/Mutations/MfaAssociate.gql'
import MfaVerifyMutation from './Schema/Mutations/MfaVerify.gql'
import MfaChallengeMutation from './Schema/Mutations/MfaChallenge.gql'
import MfaDisableMutation from './Schema/Mutations/MfaDisable.gql'

import { createUser } from '@/models/User'
import { createTokens } from '@/models/Tokens'

import {
	OBTAIN_TOKENS,
	INSPECT_TOKENS,
	FETCH_USER_DATA,
	REFRESH_TOKENS,
	DESTROY_SESSION,
	PASSWORD_CHALLENGE,
	UPDATE_ACCOUNT_DATA,
	CHANGE_PASSWORD,
	SET_DEPARTMENT,
	SET_SHIPPING_RULES,
	REQUEST_NEW_PASSWORD,
	RESET_PASSWORD,
	CHANGE_DEFAULT_DEPARTMENT,
	FETCH_MFA_STATUS,
	MFA_ASSOCIATE,
	MFA_VERIFY,
	MFA_CHALLENGE,
	MFA_DISABLE
} from './action-types'

import {
	SET_TOKENS,
	SET_USER,
	DESTROY_AUTH,
	UPDATE_AUTHENTICATED,
	UPDATE_VALIDATING,
	CHANGE_SHIPPING_RULES_STATUS,
	SET_CURRENTLY_SELECTING_DEPARTMENT_ID,
	SET_MFA_STATUS,
	SET_MFA_CHALLENGE_CONTEXT,
	CHANGE_DEFAULT_DEPARTMENT as CHANGE_DEFAULT_DEPARTMENT_MUTATION
} from './mutation-types'

// Tracks an in-flight background token refresh so rapid navigations
// do not trigger multiple concurrent refresh mutations.
let refreshInFlight = null

// Tracks an in-flight MFA status request so the middleware and views can
// call it concurrently without duplicating network calls.
let mfaStatusRequest = null

const fetchMfaStatus = async commit => {
	const { data, errors } = await ApolloClient.query({
		query: MfaStatusQuery,
		// 'none' so a failing query rejects instead of resolving with a
		// null status that callers could misread as "MFA not required".
		errorPolicy: 'none'
	})

	// Defensive: never commit defaults when the status is missing —
	// doing so would silently clear required/enforcementRequired.
	if (!data?.mfaStatus) {
		throw (errors && errors[0]) || new Error('MFA status unavailable')
	}

	commit(SET_MFA_STATUS, data.mfaStatus)
	return data.mfaStatus
}

export default {

	async [CHANGE_DEFAULT_DEPARTMENT] ({ commit, state }, { selectedDepartment, selectedCompany }) {
		// First, update the state
		commit(CHANGE_DEFAULT_DEPARTMENT_MUTATION, { selectedDepartment, selectedCompany })

		// Then handle router logic
		const router = this._vm?.$router
		if (!router) {
			console.warn('Router not available in CHANGE_DEFAULT_DEPARTMENT action')
			return
		}

		// Check permissions and navigate if needed
		const currentRoute = router.currentRoute
		if (
			currentRoute.meta &&
			currentRoute.meta.permissions &&
			currentRoute.meta.permissions.length > 0
		) {
			if (
				!currentRoute.meta.permissions.every(permission =>
					state.user.hasSomePermission(permission)
				)
			) {
				router.push({ path: '/' })
			}
		}
	},

	[OBTAIN_TOKENS]: async ({ commit }, credentials) => {
		ApolloClient.clearStore()

		const { data } = await ApolloClient.mutate({
			mutation: ObtainTokensMutation,
			variables: {
				payload: credentials
			}
		})

		if (data.errors) {
			throw data.errors
		}

		const user = createUser(data.obtainTokens.user)
		commit(SET_TOKENS, createTokens(data.obtainTokens.tokens))
		commit(SET_USER, user)
		commit(UPDATE_AUTHENTICATED, true)

		// Identify user in Sentry
		Logger.setUser(user)

		return data.obtainTokens
	},

	[DESTROY_SESSION]: async ({ commit }) => {
		ApolloClient.clearStore()
		const { data } = await ApolloClient.mutate({ mutation: DestroySessionMutation })
		commit(DESTROY_AUTH)

		// Clear user from Sentry
		Logger.setUser(null)

		return data.destroySession.status
	},

	[FETCH_USER_DATA]: async ({ commit }) => {
		const { data } = await ApolloClient.query({ query: FetchUserDataQuery })
		const user = createUser(data.user)
		commit(UPDATE_AUTHENTICATED, true)
		commit(SET_USER, user)

		// Identify user in Sentry
		Logger.setUser(user)
	},

	[INSPECT_TOKENS]: async ({ commit, state, dispatch }) => {
		const accessToken = state.tokens.accessToken

		if (!accessToken) {
			return false
		}

		try {
			const decoded = await JWTDecode(accessToken)
			const secondsUntilExpiry = decoded.exp - Date.now() / 1000

			if (secondsUntilExpiry < 0) {
				// Token already expired: refresh must complete before navigation proceeds
				commit(UPDATE_VALIDATING, true)
				await dispatch(REFRESH_TOKENS)
				commit(UPDATE_VALIDATING, false)
				return state.authenticated
			}

			if (secondsUntilExpiry < 30 && !refreshInFlight) {
				// Token expiring soon: refresh in the background without blocking navigation.
				// Apollo's TokenRefreshLink covers any request made with the stale token meanwhile.
				refreshInFlight = dispatch(REFRESH_TOKENS)
					.catch(err => {
						Logger.captureException(err, { action: 'background_token_refresh' })
					})
					.finally(() => {
						refreshInFlight = null
					})
			}

			if (state.authenticated !== true) {
				commit(UPDATE_VALIDATING, true)
				await dispatch(FETCH_USER_DATA)
				commit(UPDATE_VALIDATING, false)
				return state.authenticated
			}
		} catch (error) {
			if (!error.message?.includes('Failed to fetch')) {
				commit(DESTROY_AUTH)

				// Clear user from Sentry on auth error
				Logger.setUser(null)
			}
			commit(UPDATE_VALIDATING, false)
			return error
		}
	},

	[REFRESH_TOKENS]: async ({ commit, state }) => {
		try {
			const { data } = await ApolloClient.mutate({
				mutation: RefreshTokensMutation,
				variables: {
					payload: {
						refreshToken: state.tokens.refreshToken
					}
				}
			})

			const user = createUser(data.refreshTokens.user)
			commit(SET_TOKENS, createTokens(data.refreshTokens.tokens))
			commit(SET_USER, user)
			commit(UPDATE_AUTHENTICATED, true)

			// Identify user in Sentry
			Logger.setUser(user)

			return data.refreshTokens
		} catch (error) {
			commit(DESTROY_AUTH)
			commit(UPDATE_VALIDATING, false)

			// Clear user from Sentry on refresh error
			Logger.setUser(null)

			return error.graphQLErrors?.length
				? error.graphQLErrors.map(error => error.message)
				: [error.message]
		}
	},

	[PASSWORD_CHALLENGE]: async ({ commit }, payload) => {
		const { data } = await ApolloClient.mutate({
			mutation: PasswordChallengeMutation,
			variables: {
				payload
			}
		})

		if (data.errors) {
			throw data.errors
		}

		const user = createUser(data.passwordChallenge.user)
		commit(SET_TOKENS, createTokens(data.passwordChallenge.tokens))
		commit(SET_USER, user)
		commit(UPDATE_AUTHENTICATED, true)

		// Identify user in Sentry
		Logger.setUser(user)

		return data.passwordChallenge
	},

	[UPDATE_ACCOUNT_DATA]: async ({ commit }, payload) => {
		const { data } = await ApolloClient.mutate({
			mutation: EditUserMutation,
			variables: {
				payload
			}
		})

		const user = createUser(data.editUser)
		commit(SET_USER, user)

		// Update user in Sentry (in case email/name changed)
		Logger.setUser(user)

		return user
	},

	[CHANGE_PASSWORD]: async ({ dispatch }, payload) => {
		const mutation = {
			mutation: ChangePasswordMutation,
			variables: {
				payload
			}
		}
		await ApolloClient.mutate(mutation)
		return await dispatch(DESTROY_SESSION)
	},

	[SET_DEPARTMENT]: async ({ commit }, { selectedDepartment, selectedCompany }) => {
		const mutation = {
			mutation: SetDepartment,
			variables: {
				payload: {
					selectedCompany,
					selectedDepartment
				}
			}
		}
		commit(SET_CURRENTLY_SELECTING_DEPARTMENT_ID, selectedDepartment)
		ApolloClient.stop()
		ApolloClient.resetStore()
		const { data } = await ApolloClient.mutate(mutation)
		commit(CHANGE_DEFAULT_DEPARTMENT, { selectedDepartment, selectedCompany })
		commit(SET_CURRENTLY_SELECTING_DEPARTMENT_ID, null)
		return data
	},

	[SET_SHIPPING_RULES]: async ({ commit, state, dispatch }, value) => {
		commit(CHANGE_SHIPPING_RULES_STATUS, !!value)

		let userId = state.user?.id
		if (!userId) {
			try {
				await dispatch(FETCH_USER_DATA)
				userId = state.user?.id
			} catch (_e) {
				return null
			}
		}
		if (!userId) {
			return null
		}

		const mutation = {
			mutation: SetShippingRules,
			variables: {
				payload: {
					id: userId,
					shippingRulesAccepted: !!value
				}
			}
		}

		const { data } = await ApolloClient.mutate(mutation)
		return data
	},

	[REQUEST_NEW_PASSWORD]: async (_, email) => {
		const { data } = await ApolloClient.mutate({
			mutation: ForgotPassword,
			variables: {
				payload: {
					email
				}
			}
		})
		return data
	},

	[RESET_PASSWORD]: async ({ commit }, payload) => {
		const { data } = await ApolloClient.mutate({
			mutation: ResetPassword,
			variables: {
				payload
			}
		})

		const user = createUser(data.resetPassword.user)
		commit(SET_TOKENS, createTokens(data.resetPassword.tokens))
		commit(SET_USER, user)
		commit(UPDATE_AUTHENTICATED, true)

		// Identify user in Sentry
		Logger.setUser(user)

		return data.resetPassword
	},

	[FETCH_MFA_STATUS]: async ({ commit }) => {
		if (mfaStatusRequest) {
			return await mfaStatusRequest
		}

		mfaStatusRequest = fetchMfaStatus(commit)
			.finally(() => {
				mfaStatusRequest = null
			})

		return await mfaStatusRequest
	},

	// Returns the full otpauth:// URI to the caller only. It is never
	// committed to Vuex or persisted, and must never be logged.
	[MFA_ASSOCIATE]: async () => {
		const { data } = await ApolloClient.mutate({ mutation: MfaAssociateMutation })
		return data.mfaAssociate || {}
	},

	[MFA_VERIFY]: async ({ dispatch }, { userCode }) => {
		const mutation = {
			mutation: MfaVerifyMutation,
			variables: {
				payload: {
					userCode
				}
			}
		}

		const { data } = await ApolloClient.mutate(mutation)
		const result = data.mfaVerify
		if (result?.status) {
			// Enrollment is complete only when the authoritative status
			// refresh reports enabled=true. A failed refresh leaves the
			// existing state untouched (enforcement keeps applying).
			await dispatch(FETCH_MFA_STATUS).catch(() => {})
		}
		return result
	},

	[MFA_DISABLE]: async ({ dispatch }) => {
		const { data } = await ApolloClient.mutate({ mutation: MfaDisableMutation })
		const result = data.mfaDisable
		if (result?.status) {
			// Mirror MFA_VERIFY: refresh the authoritative status so the UI
			// reflects the disabled state. A failed refresh leaves the
			// existing state untouched.
			await dispatch(FETCH_MFA_STATUS).catch(() => {})
		}
		return result
	},

	// Completes the Cognito software-token login challenge. Tokens and the
	// full user are only committed after a successful challenge — the caller is
	// never partially authenticated.
	[MFA_CHALLENGE]: async ({ commit }, payload) => {
		const { data } = await ApolloClient.mutate({
			mutation: MfaChallengeMutation,
			variables: {
				payload
			}
		})

		const user = createUser(data.mfaChallenge.user)
		commit(SET_TOKENS, createTokens(data.mfaChallenge.tokens))
		commit(SET_USER, user)
		commit(UPDATE_AUTHENTICATED, true)
		// Drop the transient Cognito challenge session.
		commit(SET_MFA_CHALLENGE_CONTEXT, null)

		// Identify user in Sentry
		Logger.setUser(user)

		return data.mfaChallenge
	}
}
